import { memo } from 'react';
import { Chip, type IconPath } from '@nespresso-design-system/components';
import { useRef, useState, useEffect } from 'preact/hooks';
import cn from 'classnames';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape.tsx';
import useMediaQuery from '@/widgets/hooks/useMediaQuery.tsx';
import { AllFiltersButton } from '@/widgets/ProductList/drawers/AllFiltersDrawer/AllFiltersDrawer.tsx';
import {
  trackFiltersApplied,
  trackFiltersRemoval
} from '@/widgets/ProductList/Tracking.ts';
import type { FiltersAndSort } from '@/widgets/services/FilteringAndSortingService.ts';
import styles from './FilterGroup.module.scss';
import FilterPicker from '../FilterPicker/FilterPicker.tsx';
import type { Filter } from '@/domain/FilteringAndSorting.ts';
import SortPicker from '../SortPicker/SortPicker.tsx';
import useRoute from '@/widgets/hooks/useRoute.tsx';

export type FilterGroupProps = {
  filtersAndSort: FiltersAndSort;
};

type FilterGroupItemProps = {
  filter: Filter;
  filtering: FiltersAndSort['filtering'];
  isSelected: boolean;
  visible?: boolean;
  expanded?: boolean;
};

function FilterGroup({ filtersAndSort }: FilterGroupProps) {
  const { isMobile } = useMediaQuery();
  const filtersWrapperRef = useRef<HTMLDivElement>(null);
  const [isAllFiltersButtonVisible, setIsAllFiltersButtonVisible] =
    useState(false);
  const [filterVisibilities, setFilterVisibilities] = useState<boolean[]>([
    ...filtersAndSort.filtering.filters.value.map(() => false)
  ]);
  const { productType } = useRoute();

  useEffect(() => {
    handleViewportResize();

    window.addEventListener('resize', handleViewportResize);

    return () => {
      window.removeEventListener('resize', handleViewportResize);
    };
  }, []);

  function computeFiltersVisibility(filtersWrapperWidthParam: number) {
    const filterVisibilitiesArr: boolean[] = [];
    let accumulatedFiltersWidth = 0;
    const filterGroupGap = 12;
    const filterGroups =
      filtersWrapperRef?.current?.firstElementChild?.children || [];

    Array.from(filterGroups).forEach((filterGroup, idx) => {
      const filterGroupEl = filterGroup as HTMLLIElement;
      accumulatedFiltersWidth += filterGroupEl.offsetWidth + filterGroupGap;

      const filterIsVisible =
        accumulatedFiltersWidth < filtersWrapperWidthParam;

      filterVisibilitiesArr[idx] = filterIsVisible;
    });

    const hideFiltersBreakpoint = 640;

    setIsAllFiltersButtonVisible(
      filterVisibilitiesArr.includes(false) ||
        window.innerWidth <= hideFiltersBreakpoint
    );

    setFilterVisibilities(filterVisibilitiesArr);
  }

  function handleViewportResize() {
    const filtersWrapperWidth =
      filtersWrapperRef?.current?.getBoundingClientRect().width;

    if (filtersWrapperWidth !== undefined) {
      computeFiltersVisibility(filtersWrapperWidth);
    }
  }

  return (
    <div class={styles.container}>
      <div
        class={cn(styles.filtersWrapper, styles[productType])}
        ref={filtersWrapperRef}
      >
        {!isMobile && (
          <ul
            class={cn(styles.filters, {
              [styles.expandedMode]: productType === 'accessory'
            })}
          >
            {filtersAndSort.filtering.filters.value.map((filter, idx) => (
              <FilterChip
                key={filter.id}
                filter={filter}
                filtering={filtersAndSort.filtering}
                visible={filterVisibilities[idx]}
                isSelected={filter.options.some((option) => option.selected)}
                expanded={productType === 'accessory'}
              />
            ))}
          </ul>
        )}
      </div>

      <div
        class={cn(styles.allFiltersButton, {
          [styles.allFiltersButtonHidden]: !isAllFiltersButtonVisible
        })}
      >
        <AllFiltersButton />
      </div>

      <SortPicker
        sorting={filtersAndSort.sorting}
        class={styles.sortPicker}
        id="productlist-sortpicker-2"
      />
    </div>
  );
}
function FilterChip({
  filter,
  filtering,
  isSelected,
  visible = true,
  expanded = false
}: FilterGroupItemProps) {
  const filterOptions = filter.options.map((option) => (
    <li
      key={`${option.id}-${option.label}`}
      class={cn(styles.option, {
        [styles.optionWithNoIcon]: !option?.icon
      })}
    >
      <Chip
        aria-label={option.label}
        label={option.label}
        icon={option.icon as IconPath}
        isActive={option.selected}
        // @ts-ignore
        intent="make-is-active-work-properly-by-disabling-the-internal-logic-of-the-chip"
        disabled={filtering.isDisabledFilterOption(filter.id, option.id).value}
        onClick={() => {
          filtering.updateFilter(filter.id, option.id, !option.selected);
          if (option.selected) {
            trackFiltersRemoval([[filter, option]], 'clear group chip');
          } else {
            trackFiltersApplied(filtering.currentFilters.value);
          }
        }}
      />
    </li>
  ));

  if (expanded) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{filterOptions}</>;
  }

  return (
    <li
      class={cn({
        [styles.filterInvisible]: !visible,
        [styles.optionWithNoIcon]: !filter?.options[0]?.icon
      })}
    >
      {filter.type === 'QUICK' ? (
        <Chip
          aria-label={filter.options[0].label}
          label={filter.options[0].label}
          // @ts-ignore
          intent="make-is-active-work-properly-by-disabling-the-internal-logic-of-the-chip"
          isActive={filtering.isFilteredBy(filter.id).value}
          disabled={
            filtering.isDisabledFilterOption(filter.id, filter.options[0].id)
              .value
          }
          onClick={() => {
            const option = filter.options[0];
            filtering.updateFilter(filter.id, option.id, !option.selected);

            const currentFilters = filtering.currentFilters.value;

            if (option.selected) {
              trackFiltersRemoval(currentFilters, 'clear group chip');
            } else {
              trackFiltersApplied(currentFilters);
            }
          }}
          icon={filter.options[0].icon as IconPath}
        />
      ) : (
        <FilterPicker filter={filter} position="left" isSelected={isSelected}>
          <ul class={styles.optionsList}>{filterOptions}</ul>
        </FilterPicker>
      )}
    </li>
  );
}

function Skeleton() {
  return (
    <div class={styles.container}>
      <SkeletonShape class={styles.skeleton} />
    </div>
  );
}
FilterGroup.Skeleton = Skeleton;
const FilterGroupMemoed = memo(FilterGroup); // TODO: Remove memo!
FilterGroupMemoed.Skeleton = Skeleton;
export default FilterGroupMemoed;
